import {
  ArrowLeft,
  Download,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Pencil,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminDocumenti,
  useEliminaDocumento,
  useUpdateDocumento,
  useUploadDocumento,
} from "@/hooks/useAdminDocumenti";
import { useMe } from "@/hooks/useMe";
import { ApiError, apiDownloadFile } from "@/lib/api";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type {
  DocCategoria,
  Documento,
  DocumentoUpdate,
  DocVisibilita,
} from "@/types/api";

const CATEGORIE: { value: DocCategoria; label: string }[] = [
  { value: "contratto", label: "Contratto" },
  { value: "addendum", label: "Addendum" },
  { value: "nda", label: "NDA" },
  { value: "fattura", label: "Fattura" },
  { value: "verbale", label: "Verbale" },
  { value: "tecnico", label: "Documento tecnico" },
  { value: "report_sla", label: "Report SLA" },
  { value: "materiale_formativo", label: "Materiale formativo" },
  { value: "altro", label: "Altro" },
];

function categoriaLabel(c: DocCategoria): string {
  return CATEGORIE.find((x) => x.value === c)?.label ?? c;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export function AdminDocumentiPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: documenti = [], isLoading } = useAdminDocumenti();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editing, setEditing] = useState<Documento | null>(null);

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Documenti
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Carica, organizza e governa i documenti di progetto. <span className="font-medium">solo_maitime</span> = visibile solo a noi; <span className="font-medium">cliente_e_maitime</span> = visibile anche al cliente nella sezione Documenti del portale.
        </p>
      </header>

      <UploadDropzone onFile={setPendingFile} />

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : documenti.length === 0 ? (
        <div className="rounded-lg border bg-card/40 p-8 text-center text-sm text-muted-foreground">
          Nessun documento caricato. Trascina un file nella zona qui sopra.
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs">
              <tr>
                <th className="text-left font-medium px-4 py-2">Nome</th>
                <th className="text-left font-medium px-4 py-2 w-[140px]">Categoria</th>
                <th className="text-left font-medium px-4 py-2 w-[120px]">Visibilità</th>
                <th className="text-left font-medium px-4 py-2 w-[80px]">Tipo</th>
                <th className="text-right font-medium px-4 py-2 w-[80px]">Dimens.</th>
                <th className="text-left font-medium px-4 py-2 w-[110px]">Caricato</th>
                <th className="text-right font-medium px-4 py-2 w-[120px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {documenti.map((d) => (
                <DocumentoRow
                  key={d.id}
                  documento={d}
                  onEdit={() => setEditing(d)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingFile && (
        <UploadDialog
          file={pendingFile}
          onClose={() => setPendingFile(null)}
        />
      )}
      {editing && (
        <EditDialog documento={editing} onClose={() => setEditing(null)} />
      )}
    </PageContainer>
  );
}

// ── Dropzone ─────────────────────────────────────────────────────────

function UploadDropzone({ onFile }: { onFile: (f: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > MAX_UPLOAD_BYTES) {
      alert(
        `File troppo grande: ${formatBytes(file.size)}. Limite ${formatBytes(MAX_UPLOAD_BYTES)}.`
      );
      return;
    }
    onFile(file);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className={cn(
        "rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer",
        dragging
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/20 bg-card/40 hover:bg-card hover:border-muted-foreground/40"
      )}
    >
      <UploadCloud className="size-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-sm font-medium text-foreground">
        Trascina un file qui o clicca per scegliere
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        Max {formatBytes(MAX_UPLOAD_BYTES)} · PDF, immagini, Office, testi
      </p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

// ── Riga documento ───────────────────────────────────────────────────

function DocumentoRow({
  documento: d,
  onEdit,
}: {
  documento: Documento;
  onEdit: () => void;
}) {
  const elimina = useEliminaDocumento();

  async function handleDownload() {
    try {
      await apiDownloadFile(
        `/api/v1/documenti/${d.id}/download`,
        d.nome_file_originale
      );
    } catch (err) {
      if (err instanceof ApiError) {
        alert(`Errore download: ${err.message}`);
      } else {
        alert("Errore download del file.");
      }
    }
  }

  function handleDelete() {
    if (!confirm(`Eliminare il documento "${d.nome}"? Il file binario resta su storage per audit; la riga sarà nascosta.`)) {
      return;
    }
    elimina.mutate(d.id);
  }

  const ext = d.nome_file_originale.split(".").pop()?.toLowerCase() ?? "";

  return (
    <tr className={cn(elimina.isPending && "opacity-50")}>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate">{d.nome}</div>
            {d.descrizione && (
              <div className="text-xs text-muted-foreground truncate">
                {d.descrizione}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground/70 font-mono mt-0.5 truncate">
              {d.nome_file_originale}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5 text-xs">
        <Badge variant="outline" className="text-[10px]">
          {categoriaLabel(d.categoria)}
        </Badge>
      </td>
      <td className="px-4 py-2.5">
        {d.visibilita === "cliente_e_maitime" ? (
          <Badge
            variant="outline"
            className="text-[10px] bg-success/10 text-success border-success/30"
          >
            <Eye className="size-3 mr-1" />
            cliente + Maitime
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
            <EyeOff className="size-3 mr-1" />
            solo Maitime
          </Badge>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs uppercase font-mono text-muted-foreground">
        {ext || "—"}
      </td>
      <td className="px-4 py-2.5 text-right text-xs text-muted-foreground tabular-nums">
        {formatBytes(d.dimensione_bytes)}
      </td>
      <td className="px-4 py-2.5 text-xs text-muted-foreground">
        {formatData(d.caricato_il)}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            title="Scarica"
          >
            <Download className="size-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} title="Modifica metadati">
            <Pencil className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={elimina.isPending}
            title="Elimina"
          >
            {elimina.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Trash2 className="size-3.5 text-destructive" />
            )}
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── Dialog upload ────────────────────────────────────────────────────

function UploadDialog({
  file,
  onClose,
}: {
  file: File;
  onClose: () => void;
}) {
  const upload = useUploadDocumento();
  const [nome, setNome] = useState(file.name.replace(/\.[^.]+$/, ""));
  const [categoria, setCategoria] = useState<DocCategoria>("tecnico");
  const [descrizione, setDescrizione] = useState("");
  const [visibilita, setVisibilita] = useState<DocVisibilita>("solo_maitime");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    upload.mutate(
      { file, categoria, nome: nome.trim(), descrizione: descrizione.trim() || undefined, visibilita },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Carica documento</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs">{file.name}</span> ({formatBytes(file.size)})
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Nome (visibile)</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              maxLength={200}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Categoria</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as DocCategoria)}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {CATEGORIE.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Descrizione (opzionale)</label>
            <Textarea
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              rows={2}
              placeholder="Es: addendum firmato 12 maggio, copia inviata via mail a Enzo"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Visibilità</label>
            <div className="flex gap-2">
              <label
                className={cn(
                  "flex-1 rounded-md border px-3 py-2 text-xs cursor-pointer transition-colors",
                  visibilita === "solo_maitime"
                    ? "bg-muted/60 border-muted-foreground/30"
                    : "bg-card hover:bg-muted/30"
                )}
              >
                <input
                  type="radio"
                  name="visibilita"
                  value="solo_maitime"
                  checked={visibilita === "solo_maitime"}
                  onChange={() => setVisibilita("solo_maitime")}
                  className="sr-only"
                />
                <div className="flex items-center gap-1.5">
                  <EyeOff className="size-3.5" />
                  <span className="font-medium">solo Maitime</span>
                </div>
              </label>
              <label
                className={cn(
                  "flex-1 rounded-md border px-3 py-2 text-xs cursor-pointer transition-colors",
                  visibilita === "cliente_e_maitime"
                    ? "bg-success/10 border-success/40 text-success"
                    : "bg-card hover:bg-success/5"
                )}
              >
                <input
                  type="radio"
                  name="visibilita"
                  value="cliente_e_maitime"
                  checked={visibilita === "cliente_e_maitime"}
                  onChange={() => setVisibilita("cliente_e_maitime")}
                  className="sr-only"
                />
                <div className="flex items-center gap-1.5">
                  <Eye className="size-3.5" />
                  <span className="font-medium">cliente + Maitime</span>
                </div>
              </label>
            </div>
          </div>

          {upload.error instanceof ApiError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
              {upload.error.message}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={upload.isPending || !nome.trim()}>
              {upload.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Carica
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog modifica ──────────────────────────────────────────────────

function EditDialog({
  documento,
  onClose,
}: {
  documento: Documento;
  onClose: () => void;
}) {
  const update = useUpdateDocumento();
  const [nome, setNome] = useState(documento.nome);
  const [categoria, setCategoria] = useState<DocCategoria>(documento.categoria);
  const [descrizione, setDescrizione] = useState(documento.descrizione ?? "");
  const [visibilita, setVisibilita] = useState<DocVisibilita>(documento.visibilita);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: DocumentoUpdate = {};
    if (nome.trim() !== documento.nome) body.nome = nome.trim();
    if (categoria !== documento.categoria) body.categoria = categoria;
    if ((descrizione.trim() || null) !== documento.descrizione)
      body.descrizione = descrizione.trim() || null;
    if (visibilita !== documento.visibilita) body.visibilita = visibilita;

    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    update.mutate({ id: documento.id, body }, { onSuccess: () => onClose() });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifica metadati</DialogTitle>
          <DialogDescription>
            Aggiorna i metadati del documento (il file binario non viene sostituito).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Nome</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              maxLength={200}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Categoria</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as DocCategoria)}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {CATEGORIE.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Descrizione</label>
            <Textarea
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Visibilità</label>
            <div className="flex gap-2">
              <label
                className={cn(
                  "flex-1 rounded-md border px-3 py-2 text-xs cursor-pointer transition-colors",
                  visibilita === "solo_maitime"
                    ? "bg-muted/60 border-muted-foreground/30"
                    : "bg-card hover:bg-muted/30"
                )}
              >
                <input
                  type="radio"
                  name="visibilita-edit"
                  checked={visibilita === "solo_maitime"}
                  onChange={() => setVisibilita("solo_maitime")}
                  className="sr-only"
                />
                <div className="flex items-center gap-1.5">
                  <EyeOff className="size-3.5" />
                  <span className="font-medium">solo Maitime</span>
                </div>
              </label>
              <label
                className={cn(
                  "flex-1 rounded-md border px-3 py-2 text-xs cursor-pointer transition-colors",
                  visibilita === "cliente_e_maitime"
                    ? "bg-success/10 border-success/40 text-success"
                    : "bg-card hover:bg-success/5"
                )}
              >
                <input
                  type="radio"
                  name="visibilita-edit"
                  checked={visibilita === "cliente_e_maitime"}
                  onChange={() => setVisibilita("cliente_e_maitime")}
                  className="sr-only"
                />
                <div className="flex items-center gap-1.5">
                  <Eye className="size-3.5" />
                  <span className="font-medium">cliente + Maitime</span>
                </div>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
